import React, { useState, useEffect, useCallback } from 'react';
import MenuManagementView from '../../components/domain/MenuManagementView';
import * as domainApi from '../../lib/api/domain';

const MenuContainer = () => {
  const [menuList, setMenuList] = useState([]);
  const [parentMenus, setParentMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [historyMode, setHistoryMode] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [formData, setFormData] = useState({
    menuUuid: '',
    menuId: '',
    menuTitle: '',
    menuLink: '',
    menuIcon: '',
    menuExpand: true,
    menuChecked: true,
    menuAdmin: false,
    parentUuid: '',
    menuOrder: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expanded, setExpanded] = useState([]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 메뉴 목록 조회
  const fetchMenus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await domainApi.getListMenus();
      if (response.data?.list) {
        setMenuList(response.data.list);
        const parents = response.data.list.filter(
          (menu) => !menu.parentUuid
        );
        setParentMenus(parents);
        setExpanded(parents.map((p) => p.menuUuid));
      }
    } catch (error) {
      console.error('메뉴 조회 에러:', error);
      showSnackbar('메뉴 데이터를 가져오는 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  // 메뉴 이력 조회
  const fetchMenuHistory = async (menuUuid) => {
    try {
      setLoading(true);
      const response = await domainApi.getListMenuHistorys(menuUuid);
      if (response.data?.list) {
        setHistoryData(response.data.list);
      }
    } catch (error) {
      console.error('메뉴 이력 조회 에러:', error);
      showSnackbar('메뉴 이력 조회 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 메뉴 생성
  const createMenu = async () => {
    try {
      setLoading(true);
      const menuId = parseInt(formData.menuId);
      if (isNaN(menuId)) {
        showSnackbar('메뉴 ID는 숫자여야 합니다.', 'error');
        return;
      }
      const requestData = {
        ...formData,
        menuId,
        menuOrder: parseInt(formData.menuOrder) || 0,
      };
      const response = await domainApi.addMenuInfo(requestData);
      if (response.data?.result === 'success') {
        showSnackbar('메뉴가 성공적으로 생성되었습니다.', 'success');
        fetchMenus();
        setOpenDialog(false);
      } else {
        showSnackbar('메뉴 생성 중 오류가 발생했습니다.', 'error');
      }
    } catch (error) {
      console.error('메뉴 생성 에러:', error);
      showSnackbar('메뉴 생성 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 메뉴 수정
  const updateMenu = async () => {
    try {
      setLoading(true);
      const menuId = parseInt(formData.menuId);
      if (isNaN(menuId)) {
        showSnackbar('메뉴 ID는 숫자여야 합니다.', 'error');
        return;
      }
      const requestData = {
        ...formData,
        menuId,
        menuOrder: parseInt(formData.menuOrder) || 0,
      };
      const response = await domainApi.editMenuInfo(requestData);
      if (response.data?.result === 'success') {
        showSnackbar('메뉴가 성공적으로 수정되었습니다.', 'success');
        fetchMenus();
        setOpenDialog(false);
      } else {
        showSnackbar('메뉴 수정 중 오류가 발생했습니다.', 'error');
      }
    } catch (error) {
      console.error('메뉴 수정 에러:', error);
      showSnackbar('메뉴 수정 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 메뉴 삭제
  const deleteMenu = async (menuUuid) => {
    if (!window.confirm('정말로 이 메뉴를 삭제하시겠습니까?')) return;
    try {
      setLoading(true);
      const response = await domainApi.removeMenuInfo(menuUuid);
      if (response.data?.result === 'success') {
        showSnackbar('메뉴가 성공적으로 삭제되었습니다.', 'success');
        fetchMenus();
      } else {
        showSnackbar('메뉴 삭제 중 오류가 발생했습니다.', 'error');
      }
    } catch (error) {
      console.error('메뉴 삭제 에러:', error);
      showSnackbar('메뉴 삭제 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 신규 추가 (최상위)
  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setFormData({
      menuUuid: '', menuId: '', menuTitle: '', menuLink: '', menuIcon: '',
      menuExpand: true, menuChecked: true, menuAdmin: false, parentUuid: '', menuOrder: 0,
    });
    setOpenDialog(true);
  };

  // 하위 메뉴 추가 (상위 메뉴 자동 선택)
  const handleAddChildMenu = (parentMenu) => {
    setDialogMode('add');
    setFormData({
      menuUuid: '', menuId: '', menuTitle: '', menuLink: '', menuIcon: '',
      menuExpand: true, menuChecked: true, menuAdmin: false,
      parentUuid: parentMenu.menuUuid,
      menuOrder: 0,
    });
    setOpenDialog(true);
  };

  // 수정
  const handleOpenEditDialog = (menu) => {
    setDialogMode('edit');
    setFormData({
      menuUuid: menu.menuUuid,
      menuId: menu.menuId?.toString() || '',
      menuTitle: menu.menuTitle || '',
      menuLink: menu.menuLink || '',
      menuIcon: menu.menuIcon || '',
      menuExpand: menu.menuExpand ?? true,
      menuChecked: menu.menuChecked ?? true,
      menuAdmin: menu.menuAdmin ?? false,
      parentUuid: menu.parentUuid || '',
      menuOrder: menu.menuOrder || 0,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleOpenHistory = (menuUuid) => {
    fetchMenuHistory(menuUuid);
    setHistoryMode(true);
  };

  const handleCloseHistory = () => {
    setHistoryMode(false);
    setHistoryData([]);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.menuId || !formData.menuTitle) {
      showSnackbar('메뉴 ID와 메뉴명은 필수 입력 항목입니다.', 'error');
      return;
    }
    if (dialogMode === 'add') {
      createMenu();
    } else {
      updateMenu();
    }
  };

  const handleToggle = (_, nodeIds) => setExpanded(nodeIds);

  // 메뉴 상세 보기
  const handleViewMenu = (menu) => {
    setSelectedMenu(selectedMenu?.menuUuid === menu.menuUuid ? null : menu);
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <MenuManagementView
      menuList={menuList}
      parentMenus={parentMenus}
      loading={loading}
      selectedMenu={selectedMenu}
      openDialog={openDialog}
      dialogMode={dialogMode}
      historyMode={historyMode}
      historyData={historyData}
      formData={formData}
      snackbar={snackbar}
      expanded={expanded}
      handleOpenAddDialog={handleOpenAddDialog}
      handleAddChildMenu={handleAddChildMenu}
      handleOpenEditDialog={handleOpenEditDialog}
      handleCloseDialog={handleCloseDialog}
      handleFormChange={handleFormChange}
      handleSubmit={handleSubmit}
      handleDelete={deleteMenu}
      handleOpenHistory={handleOpenHistory}
      handleCloseHistory={handleCloseHistory}
      handleToggle={handleToggle}
      handleViewMenu={handleViewMenu}
      handleCloseSnackbar={handleCloseSnackbar}
    />
  );
};

export default MenuContainer;
